/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { ProminentSearchWithScanner } from '../../ui/public-api';
import type { ProminentSearchWithScannerProps } from '../../ui/public-api';
import {
  effectiveProductSearchQuery,
  MIN_PRODUCT_SEARCH_QUERY_CHARS,
  PRODUCT_SEARCH_DEBOUNCE_MS,
  productSearchEmptyMessage,
} from '../productSearchUx';
import { ProductSearchPinnedChip } from './ProductSearchPinnedChip';
import { ProductSearchSuggestionsPanel } from './ProductSearchSuggestionsPanel';

export type ProductSearchRow = {
  id: string;
  name: string;
  sku: string;
  barcode?: string | null;
  stockId: string | null;
  stockQuantity: number | null;
  stockMinQuantity: number | null;
};

type SearchProductsFn<T extends ProductSearchRow> = (params: {
  search: string;
  limit: number;
  offset: number;
  branchId: string | null | undefined;
  posExcludeMisc?: boolean;
}) => Promise<T[]>;

export type CatalogStockProductSearchFieldsProps<T extends ProductSearchRow = ProductSearchRow> = Pick<
  ProminentSearchWithScannerProps,
  | 'value'
  | 'onChangeText'
  | 'placeholder'
  | 'placeholderCompact'
  | 'compactTitleBreakpoint'
  | 'variant'
  | 'accessibilityLabel'
  | 'onSubmitEditing'
  | 'autoFocusOnWeb'
  | 'showHidHint'
  | 'endSlot'
  | 'textInputProps'
  | 'unifiedField'
  | 'unifiedBoxStyle'
  | 'inputStyle'
  | 'scanTone'
  | 'inputRef'
> & {
  branchId: string | null | undefined;
  posExcludeMisc?: boolean;
  onPressScan?: () => void;
  showBarcodeCameraButton?: boolean;
  sectionLabel?: string | null;
  searchProductsFn?: SearchProductsFn<T>;
  onPickSuggestion?: (row: T) => void;
  pinnedProduct?: { label: string } | null;
  pinnedCaption?: string;
  onClearPinnedProduct?: () => void;
  /**
   * Controles a la derecha del campo (toolbar PC).
   * Campo + controles en una fila; sugerencias y chip pinned debajo a ancho completo.
   */
  toolbarEnd?: React.ReactNode;
  /**
   * Hint del panel de sugerencias cuando no hay coincidencias.
   * `null` = no mostrar (usar cuando el listado ya tiene su propio empty con acciones).
   */
  suggestionsEmptyMessage?: string | null;
};

/**
 * Buscar producto como en Informes › Ventas: debounce + mínimo de caracteres + sugerencias `searchProducts`.
 * SCAN sigue gestionándose fuera (modal padre): solo `onPressScan`.
 */
export const CatalogStockProductSearchFields = <T extends ProductSearchRow = ProductSearchRow>({
  value,
  onChangeText,
  branchId,
  posExcludeMisc = true,
  searchProductsFn,
  onPickSuggestion,
  onPressScan,
  showBarcodeCameraButton = true,
  sectionLabel,
  pinnedProduct = null,
  pinnedCaption = 'Filtrando por',
  onClearPinnedProduct,
  toolbarEnd,
  suggestionsEmptyMessage,
  ...rest
}: CatalogStockProductSearchFieldsProps<T>) => {
  const [debouncedDraft, setDebouncedDraft] = useState('');
  const [suggestions, setSuggestions] = useState<T[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedDraft(value.trim()), PRODUCT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    const q = effectiveProductSearchQuery(debouncedDraft);
    if (!q || !branchId || !searchProductsFn) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    let cancelled = false;
    setSuggestionsLoading(true);
    void searchProductsFn({
      search: q,
      limit: 22,
      offset: 0,
      branchId,
      posExcludeMisc,
    })
      .then((rows) => {
        if (!cancelled) setSuggestions(rows.slice(0, 20));
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setSuggestionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedDraft, branchId, posExcludeMisc, searchProductsFn]);

  const panelOpen = debouncedDraft.length >= MIN_PRODUCT_SEARCH_QUERY_CHARS;

  const suggestionFooter = (
    <ProductSearchSuggestionsPanel
      open={panelOpen}
      loading={suggestionsLoading}
      items={suggestions.map((item) => ({
        id: item.id,
        title: item.name,
        subtitle: item.sku ? `SKU · ${item.sku}` : null,
      }))}
      emptyMessage={
        suggestionsEmptyMessage === undefined
          ? productSearchEmptyMessage(Boolean(showBarcodeCameraButton && onPressScan))
          : suggestionsEmptyMessage
      }
      onPick={(row) => {
        const match = suggestions.find((s) => s.id === row.id);
        if (match) onPickSuggestion?.(match);
      }}
    />
  );

  const pinned =
    pinnedProduct?.label && onClearPinnedProduct ? (
      <ProductSearchPinnedChip
        caption={pinnedCaption}
        productLabel={pinnedProduct.label}
        onClear={onClearPinnedProduct}
      />
    ) : null;

  const field = (
    <ProminentSearchWithScanner
      value={value}
      onChangeText={onChangeText}
      onPressScan={onPressScan}
      showBarcodeCameraButton={showBarcodeCameraButton}
      sectionLabel={sectionLabel}
      unifiedField
      scanTone="neutral"
      footer={toolbarEnd ? undefined : suggestionFooter}
      {...rest}
    />
  );

  if (toolbarEnd) {
    return (
      <View style={styles.deskRoot}>
        <View style={styles.deskToolbarRow}>
          <View style={styles.deskField}>{field}</View>
          {toolbarEnd}
        </View>
        {suggestionFooter}
        {pinned}
      </View>
    );
  }

  return (
    <>
      {field}
      {pinned}
    </>
  );
};

const styles = StyleSheet.create({
  deskRoot: {
    width: '100%',
    gap: 0,
  },
  deskToolbarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  deskField: {
    flex: 1,
    minWidth: 0,
  },
});
